import React, { useState, useEffect, useCallback } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PAGE_SIZE } from "../../../api";
import Icon from "../../../components/Icon";
import Tooltip from "../../../components/Tooltip";
import Skeleton from "../../../components/Skeleton";
import Modal from "../../../components/Modal";
import { EmptyState, ErrorState } from "../../../components/State";
import Pagination from "../../../components/Pagination";
import { RCAPanel } from "../../../components/TicketDetailModal";
import {
  fetchRcaCharts,
  fetchRcaRecords,
  fetchRcaStats,
  getRcaFilterOptions,
} from "../services/RCAService";

const CHART_COLORS = {
  primary: "#1D4ED8",
  success: "#16A34A",
  grid: "#F1F5F9",
  axis: "#94A3B8",
  axisLabel: "#64748B",
};

const VALIDATION_CHART_COLORS = {
  Validated: "#16A34A",
  "Auto-validated": "#2563EB",
  "Pending review": "#D97706",
  Rejected: "#DC2626",
};

const VALIDATION_MODIFIERS = {
  Validated: "success",
  "Auto-validated": "info",
  "Pending review": "warning",
  Rejected: "danger",
};

const EMPTY_FILTERS = { search: "", issueType: "", validation: "", autoResolution: "" };

function ValidationBadge({ value }) {
  const label = value || "—";
  const modifier = VALIDATION_MODIFIERS[value] || "neutral";
  return <span className={`rca-pill rca-pill--${modifier}`}>{label}</span>;
}

function ResolutionBadge({ value }) {
  return value === "Yes" ? (
    <span className="rca-pill rca-pill--success">Auto</span>
  ) : (
    <span className="rca-pill rca-pill--neutral">Manual</span>
  );
}

function ConfidenceCell({ value }) {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  const level = safeValue >= 85 ? "high" : safeValue >= 70 ? "medium" : "low";
  return (
    <div className="rca-confidence">
      <span className="rca-confidence__track">
        <span
          className={`rca-confidence__fill rca-confidence__fill--${level}`}
          style={{ width: `${Math.max(0, Math.min(100, safeValue))}%` }}
        />
      </span>
      <span className={`rca-confidence__value rca-confidence__value--${level}`}>
        {Number.isFinite(numericValue) ? `${safeValue}%` : "—"}
      </span>
    </div>
  );
}

function ChartCard({ title, subtitle, height = 220, children }) {
  return (
    <div className="rca-chart-card">
      <div className="rca-chart-card__head">
        <div className="rca-chart-card__title">{title}</div>
        {subtitle && <div className="rca-chart-card__subtitle">{subtitle}</div>}
      </div>
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

function EmptyChartState({ message }) {
  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#64748B",
        fontSize: 12,
        textAlign: "center",
        padding: "12px",
      }}
    >
      {message}
    </div>
  );
}

export default function RCAReportView() {
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  const [stats, setStats] = useState({
    total: 0,
    avg_confidence: 0,
    auto_resolvable: 0,
    auto_resolvable_pct: 0,
    validated: 0,
    rejected: 0,
  });
  const [charts, setCharts] = useState({
    issueTypes: [],
    confidence: [],
    overTime: [],
    validation: [],
  });
  const [options, setOptions] = useState({ issueTypes: [], validations: [] });

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const loadAggregates = useCallback(async () => {
    try {
      const [statsData, chartsData, optionsData] = await Promise.all([
        fetchRcaStats(),
        fetchRcaCharts(),
        getRcaFilterOptions(),
      ]);
      if (statsData) setStats(statsData);
      if (chartsData) setCharts(chartsData);
      console.log("RCA CHARTS options",chartsData);
      if (optionsData) setOptions(optionsData);
    } catch (e) {
      console.error("Could not load RCA aggregates:", e);
    }
  }, []);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRcaRecords({ page, pageSize: PAGE_SIZE, filters: appliedFilters });
      setRecords(data?.items || []);
      setTotalCount(data?.total || 0);
    } catch {
      setError("Could not load RCA records.");
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    loadAggregates();
  }, [loadAggregates]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (appliedFilters.search !== filters.search) {
        setAppliedFilters((prev) => ({ ...prev, search: filters.search }));
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [filters.search, appliedFilters.search]);

  const handleSelectChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setAppliedFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const hasActiveFilter =
    appliedFilters.search ||
    appliedFilters.issueType ||
    appliedFilters.validation ||
    appliedFilters.autoResolution;

  const statCards = [
    { key: "total", label: "Total RCAs", value: stats.total, icon: "search", tone: "info" },
    {
      key: "confidence",
      label: "Avg confidence",
      value: `${stats.avg_confidence}%`,
      icon: "info",
      tone: "primary",
    },
    {
      key: "auto",
      label: "Auto-resolvable",
      value: `${stats.auto_resolvable_pct}%`,
      icon: "check",
      tone: "success",
    },
    {
      key: "validated",
      label: "Validated",
      value: stats.validated,
      icon: "check",
      tone: "info",
    },
    {
      key: "rejected",
      label: "Rejected",
      value: stats.rejected,
      icon: "alert",
      tone: "danger",
    },
  ];

  const tableHeaders = [
    { label: "RCA ID", tip: "Root cause analysis identifier" },
    { label: "Incident", tip: "Linked incident" },
    { label: "Root Cause", tip: "AI-diagnosed root cause" },
    { label: "Confidence", tip: "AI confidence in the diagnosis" },
    { label: "Resolution", tip: "Whether automated resolution is available" },
    { label: "Validation", tip: "Validation outcome" },
    { label: "Detected", tip: "When the incident was detected" },
  ];

  return (
    <div className="rca-report">
      <div className="rca-report__toolbar">
        <div className="rca-stat-grid">
          {statCards.map((card) => (
            <div key={card.key} className={`rca-stat-card rca-stat-card--${card.tone}`}>
              <span className="rca-stat-card__icon">
                <Icon name={card.icon} size={15} />
              </span>
              <span className="rca-stat-card__body">
                <span className="rca-stat-card__label">{card.label}</span>
                <span className="rca-stat-card__value">{card.value}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="rca-filters">
          <div className="rca-filters__search">
            <Icon name="search" size={14} color="#94A3B8" />
            <input
              placeholder="Search by RCA ID, incident, or root cause…"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            />
          </div>

          <select
            className="rca-filters__select"
            value={filters.issueType}
            onChange={(e) => handleSelectChange("issueType", e.target.value)}
          >
            <option value="">All issue types</option>
            {options.issueTypes.map((issue) => (
              <option key={issue} value={issue}>
                {issue}
              </option>
            ))}
          </select>

          <select
            className="rca-filters__select"
            value={filters.validation}
            onChange={(e) => handleSelectChange("validation", e.target.value)}
          >
            <option value="">All validation</option>
            {options.validations.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <select
            className="rca-filters__select"
            value={filters.autoResolution}
            onChange={(e) => handleSelectChange("autoResolution", e.target.value)}
          >
            <option value="">Auto &amp; manual</option>
            <option value="Yes">Auto-resolvable</option>
            <option value="No">Manual</option>
          </select>

          {hasActiveFilter && (
            <button type="button" className="rca-filters__clear" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="rca-report__body">
        <div className="rca-chart-row rca-chart-row--wide-left">
          {/* <p>{charts.issueTypes} issues</p> */}
          <ChartCard title="Issue breakdown" subtitle="RCAs grouped by reported issue">
            <BarChart data={charts.issueTypes} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid horizontal={false} stroke={CHART_COLORS.grid} />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: CHART_COLORS.axis }}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={150}
                tick={{ fontSize: 10, fill: CHART_COLORS.axisLabel }}
              />
              <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} barSize={13} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Validation outcomes" subtitle="How RCAs were validated">
            {charts.validation.length === 0 ? (
              <EmptyChartState message="No validation data available yet." />
            ) : (
              <PieChart>
                <Pie
                  data={charts.validation}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {charts.validation.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={VALIDATION_CHART_COLORS[entry.name] || CHART_COLORS.axis}
                    />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            )}
          </ChartCard>
        </div>

        <div className="rca-chart-row rca-chart-row--wide-right">
          <ChartCard title="Confidence distribution" subtitle="AI confidence, bucketed">
            {charts.confidence.length === 0 ? (
              <EmptyChartState message="No confidence distribution data available yet." />
            ) : (
              <BarChart data={charts.confidence} margin={{ left: -18, right: 8 }}>
                <CartesianGrid vertical={false} stroke={CHART_COLORS.grid} />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: CHART_COLORS.axis }} />
                <YAxis tick={{ fontSize: 10, fill: CHART_COLORS.axis }} allowDecimals={false} />
                <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="count" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} barSize={26} />
              </BarChart>
            )}
          </ChartCard>

          <ChartCard title="RCA volume over time" subtitle="Records per day">
            {charts.overTime.length === 0 ? (
              <EmptyChartState message="No time-series data available yet." />
            ) : (
              <AreaChart data={charts.overTime} margin={{ left: -18, right: 8 }}>
                <defs>
                  <linearGradient id="rcaVolumeFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={CHART_COLORS.grid} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9, fill: CHART_COLORS.axis }}
                  tickFormatter={(value) => String(value).slice(5)}
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 10, fill: CHART_COLORS.axis }} allowDecimals={false} />
                <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  fill="url(#rcaVolumeFill)"
                />
              </AreaChart>
            )}
          </ChartCard>
        </div>

        {error ? (
          <ErrorState message={error} onRetry={loadRecords} />
        ) : loading ? (
          <div className="rca-skeleton-stack">
            {[...Array(6)].map((_, index) => (
              <Skeleton key={index} height={44} style={{ borderRadius: 7 }} />
            ))}
          </div>
        ) : records.length === 0 ? (
          <EmptyState
            icon="search"
            title="No RCA records found"
            subtitle="Try adjusting your filters."
          />
        ) : (
          <div className="premium-table-container">
            <table className="premium-table">
              <thead>
                <tr>
                  {tableHeaders.map((header) => (
                    <th key={header.label}>
                      <Tooltip text={header.tip}>
                        <span className="rca-th-label">{header.label}</span>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr
                    key={record.rca_id}
                    className="rca-row"
                    onClick={() => setSelected(record)}
                  >
                    <td>
                      <span className="rca-id-chip">{String(record.rca_id).slice(0, 8)}…</span>
                    </td>
                    <td>
                      <span className="rca-mono">{record.incident_id}</span>
                    </td>
                    <td className="rca-cause-cell">
                      <div className="rca-cause-cell__title">{record.ai_root_cause}</div>
                      <div className="rca-cause-cell__sub">{record.infrastructure_dependency}</div>
                    </td>
                    <td>
                      <ConfidenceCell value={record.confidence_pct} />
                    </td>
                    <td>
                      <ResolutionBadge value={record.automated_resolution_available} />
                    </td>
                    <td>
                      <ValidationBadge value={record.validation_status} />
                    </td>
                    <td>
                      <span className="rca-muted">
                        {record.detection_timestamp
                          ? new Date(record.detection_timestamp).toLocaleDateString("en-IN")
                          : "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination page={page} total={totalCount} pageSize={PAGE_SIZE} onChange={setPage} />

      {selected && (
        <Modal title="Root Cause Analysis" onClose={() => setSelected(null)} width={680}>
          <div className="rca-modal-head">
            <div className="rca-modal-head__tags">
              <span className="rca-id-chip">{String(selected.rca_id).slice(0, 8)}…</span>
              <ResolutionBadge value={selected.automated_resolution_available} />
              <ValidationBadge value={selected.validation_status} />
            </div>
            <div className="rca-modal-head__title">
              {selected.ticket_title || selected.ai_root_cause}
            </div>
          </div>
          {selected.rca ? (
            <RCAPanel rca={selected.rca} />
          ) : (
            <EmptyState
              icon="search"
              title="No RCA available"
              subtitle="Root cause analysis hasn't been run for this ticket."
            />
          )}
        </Modal>
      )}
    </div>
  );
}
